import { useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePowerBiEmbed } from '../hooks/usePowerBiEmbed.js';
import { addFavourite, removeFavourite, clearVisualSettings } from '../api/client.js';

export default function ReportView({ kind }) {
  const { reportId } = useParams();
  const containerRef = useRef(null);
  const queryClient = useQueryClient();

  const [meta, setMeta] = useState(null);
  const [isFavourite, setIsFavourite] = useState(false);
  const [toast, setToast] = useState(null);

  const onLoaded = useCallback((config) => {
    setMeta(config);
    // The nav already knows which reports are favourites; read it from cache
    // rather than making another round trip.
    const nav = queryClient.getQueryData(['navigation', 'Desktop'])
             ?? queryClient.getQueryData(['navigation', 'Mobile']);
    const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
    setIsFavourite(!!nav?.favourites?.some(f => same(f.targetId, config.reportId)));
  }, [queryClient]);

  const { status, error, saveCurrentSlicers, reload, fullscreen, print } =
    usePowerBiEmbed({ kind, id: reportId, containerRef, onLoaded });

  const flash = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const toggleFavourite = async () => {
    const next = !isFavourite;
    setIsFavourite(next);
    try {
      await (next ? addFavourite(reportId) : removeFavourite(reportId));
      queryClient.invalidateQueries({ queryKey: ['navigation'] });
    } catch {
      setIsFavourite(!next);
      flash('Could not update favourites');
    }
  };

  const handleSaveView = async () => {
    try {
      const count = await saveCurrentSlicers();
      flash(count ? `Saved ${count} filter selection(s)` : 'No slicer selections to save');
    } catch {
      flash('Could not save your view');
    }
  };

  const handleResetView = async () => {
    try {
      await clearVisualSettings(reportId);
      reload();
      flash('View reset');
    } catch {
      flash('Could not reset your view');
    }
  };

  return (
    <div className="embed-wrapper">
      <div className="embed-toolbar">
        <button type="button" className="btn btn-sm btn-link px-1" onClick={toggleFavourite}
                aria-pressed={isFavourite}
                title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}>
          <i className={`bi ${isFavourite ? 'bi-star-fill' : 'bi-star'}`}
             style={isFavourite ? { color: 'var(--vz-orange)' } : undefined} />
        </button>
        <span className="fw-semibold text-truncate">{meta?.name ?? ''}</span>
        <div className="ms-auto d-flex align-items-center gap-1">
          {kind === 'report' && (
            <>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                      onClick={handleSaveView}>Save view</button>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                      onClick={handleResetView}>Reset</button>
            </>
          )}
          <button type="button" className="btn btn-sm btn-link" onClick={print} title="Print">
            <i className="bi bi-printer" />
          </button>
          <button type="button" className="btn btn-sm btn-link" onClick={fullscreen} title="Full screen">
            <i className="bi bi-arrows-fullscreen" />
          </button>
        </div>
      </div>

      {status === 'loading' && (
        <div className="embed-overlay"><div className="spinner-border text-primary" /></div>
      )}
      {status === 'error' && (
        <div className="embed-overlay">
          <div className="alert alert-danger" style={{ maxWidth: 520 }}>
            <strong>This report could not be loaded.</strong>
            <div className="small mt-1">
              {error?.status === 403
                ? 'You are not entitled to view this report.'
                : (error?.message ?? error?.detailedMessage ?? 'Please try again.')}
            </div>
          </div>
        </div>
      )}

      <div ref={containerRef} className="embed-container" />

      {toast && <div className="embed-toast">{toast}</div>}
    </div>
  );
}
